<template>
    <v-container fluid fill-height>
        <dynamic-toolbar
            :icon="'mdi-account-lock'"
            :title="'Permission Management'"
            color="blue-grey"
            :theme="'dark'"
            :loading="anyLoading"
        >
            <template v-slot:buttons>
                <v-btn color="success" @click="createPermission">
                    <v-icon left>mdi-plus</v-icon>
                    Create Permission
                </v-btn>
            </template>
        </dynamic-toolbar>
        <v-row no-gutters class="fill-height">
            <v-col cols="12">
                <v-card class="fill-height">
                    <v-card-text>
                        <v-data-table
                            :headers="headers"
                            :items="permissions"
                            :items-per-page="-1"
                            fixed-header
                            item-key="id"
                            items-per-page-text="Rows per page:"
                            dense
                            height="calc(100vh - 210px)"
                            :loading="loading.permissions"
                        >
                            <template v-slot:no-data>
                                <v-alert
                                    :value="true"
                                    color="error"
                                    icon="mdi-alert"
                                >
                                    No data to display
                                </v-alert>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                                {{ new Date(item.created_at).toLocaleString() }}
                            </template>
                            <template v-slot:item.assignations="{ item }">
                                <v-btn
                                    small
                                    color="indigo"
                                    plain
                                    :loading="loading.roles"
                                    @click="viewAssignations(item, 'roles')"
                                >
                                    <v-icon small left>mdi-tag</v-icon>
                                    Roles {{ item.roles.length }}
                                </v-btn>
                                <v-btn
                                    small
                                    color="cyan darken-1"
                                    plain
                                    class="ml-2"
                                    :loading="loading.users"
                                    @click="viewAssignations(item, 'users')"
                                >
                                    <v-icon small left>mdi-account</v-icon>
                                    Users
                                    {{ item.users.length }}
                                </v-btn>
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-btn
                                    class="mr-2"
                                    small
                                    plain
                                    color="warning"
                                    @click="editPermission(item)"
                                >
                                    <v-icon small>mdi-pencil</v-icon>
                                    Edit
                                </v-btn>
                                <v-btn
                                    small
                                    plain
                                    color="error"
                                    @click="deletePermission(item)"
                                >
                                    <v-icon small>mdi-delete</v-icon>
                                    Delete
                                </v-btn>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <permission-dialog
            v-model="dialog.permission"
            :permission="permission"
            :isNew="isNew"
            @create="updatePermissions('create', $event)"
            @update="updatePermissions('update', $event)"
        />
        <assignations-dialog
            v-model="dialog.assignations"
            :permission="permission"
            :catalog="catalog"
            :type="assignationType"
            @update="updatePermissionAssignations($event)"
            @delete="deletePermissionAssignations($event)"
        />
    </v-container>
</template>

<script>
import axios from 'axios';
import DynamicToolbar from '../../components/Global/Utils/DynamicToolbar.vue';
import PermissionDialog from '../../components/SystemManagement/Permissions/PermissionDialog.vue';
import AssignationsDialog from '../../components/SystemManagement/Permissions/AssignationsDialog.vue';
export default {
    name: 'Permissions',
    components: {
        DynamicToolbar,
        PermissionDialog,
        AssignationsDialog
    },
    data() {
        return {
            loading: {
                permissions: false,
                users: false,
                roles: false
            },
            headers: [
                { text: 'ID', value: 'id', width: '2%' },
                { text: 'Name', value: 'name', width: '10%' },
                { text: 'Description', value: 'description', width: '20%' },
                { text: 'Created At', value: 'created_at', width: '10%' },
                {
                    text: 'Assignations',
                    value: 'assignations',
                    sortable: false,
                    width: '10%'
                },
                {
                    text: 'Actions',
                    value: 'actions',
                    width: '10%'
                }
            ],
            permissions: [],
            users: [],
            roles: [],
            catalog: [],
            dialog: {
                permissions: false,
                assignations: false
            },
            permission: {},
            assignations: [],
            assignationType: '',
            isNew: false
        };
    },
    computed: {
        anyLoading() {
            //when any of the loading properties are true
            return Object.values(this.loading).some(value => value);
        }
    },
    mounted() {
        this.getPermissions();
    },
    methods: {
        async getPermissions() {
            this.loading.permissions = true;
            await axios
                .get('/v2/system/permissions')
                .then(response => {
                    this.permissions = response.data;
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading.permissions = false;
                });
        },
        async getUsers() {
            this.loading.users = true;
            await axios
                .get('v2/system/users')
                .then(response => {
                    this.users = response.data;
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading.users = false;
                });
        },
        async getRoles() {
            this.loading.roles = true;
            await axios
                .get('v2/system/roles')
                .then(response => {
                    this.roles = response.data;
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading.roles = false;
                });
        },
        createPermission() {
            this.dialog.permission = true;
            this.permission = null;
            this.isNew = true;
        },
        editPermission(permission) {
            this.dialog.permission = true;
            this.permission = permission;
            this.isNew = false;
        },
        deletePermission(permission) {
            console.log('Delete permission:', permission);
        },
        updatePermissions(action, permission) {
            if (action === 'create') {
                this.permissions.push(permission);
            } else if (action === 'update') {
                const index = this.permissions.findIndex(
                    p => p.id === permission.id
                );
                this.permissions.splice(index, 1, permission);
            }
        },
        updatePermissionAssignations(event) {
            let permission = event.permission;
            let assignation = event.assignation;
            let type = event.type;

            console.info(permission, assignation, type);

            let currentPermissions = this.permissions.find(
                p => p.id === permission.id
            );

            currentPermissions[type].push(assignation);
        },
        deletePermissionAssignations(event) {
            let permission = event.permission;
            let assignation = event.assignation;
            let type = event.type;

            let currentPermissions = this.permissions.find(
                p => p.id === permission.id
            );

            currentPermissions[type] = currentPermissions[type].filter(
                a => a.id !== assignation.id
            );
        },
        async viewAssignations(permission, type) {
            if (type !== 'users' && type !== 'roles') {
                return;
            } else if (type === 'users') {
                if (this.users.length === 0) {
                    await this.getUsers();
                }
                this.catalog = this.users;
            } else if (type === 'roles') {
                if (this.roles.length === 0) {
                    await this.getRoles();
                }
                this.catalog = this.roles;
            }

            this.assignationType = type;
            this.permission = permission;
            this.dialog.assignations = true;
        }
    }
};
</script>
